import { createContext, useContext, type ReactNode, type Ref } from 'react';
import { Input } from '../atoms/Input';
import styles from './DatePicker.module.css';

export interface DatePickerProps {
  /** ISO date `YYYY-MM-DD`, or `''` when empty. */
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  id?: string;
  invalid?: boolean;
  required?: boolean;
  disabled?: boolean;
  /** Shown while empty where the browser shows no format itself (iOS). */
  placeholder?: string;
  'aria-describedby'?: string;
  'aria-label'?: string;
  size?: 'sm' | 'md' | 'lg';
  ref?: Ref<HTMLInputElement>;
}

const PlaceholderContext = createContext('');

/** Registers the app's date placeholder (e.g. "TT.MM.JJJJ") once (i18n). */
export function DatePickerProvider({
  placeholder,
  children,
}: {
  placeholder: string;
  children: ReactNode;
}) {
  return <PlaceholderContext.Provider value={placeholder}>{children}</PlaceholderContext.Provider>;
}

/**
 * Date input. Uses the browser's native date picker, which is accessible,
 * localized and the best fit on phones. iOS shows an empty box for an empty
 * field, so the placeholder is drawn on top there.
 */
export function DatePicker({
  value,
  onChange,
  size = 'md',
  placeholder,
  ...rest
}: DatePickerProps) {
  const fallback = useContext(PlaceholderContext);
  const shown = placeholder ?? fallback;
  return (
    <span className={styles.control}>
      <Input
        {...rest}
        type="date"
        size={size}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={styles.date}
      />
      {value === '' && shown && (
        <span className={styles.placeholder} aria-hidden="true">
          {shown}
        </span>
      )}
    </span>
  );
}

/** Today as ISO date in the browser's time zone. */
export function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
